import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  consultarDisponibilidadeTagParceiro,
  normalizarTagParceiro,
  tagParceiroValida,
  type EstadoDisponibilidadeTag,
} from "@/lib/tag-parceiro";

export function useTagParceiroDisponibilidade({
  value,
  token,
  empresaId,
  originalValue,
}: {
  value: string;
  token?: string;
  empresaId?: string;
  originalValue?: string | null;
}) {
  const normalizada = normalizarTagParceiro(value);
  const atual = originalValue !== undefined && normalizada === normalizarTagParceiro(originalValue ?? "");
  const valida = tagParceiroValida(normalizada);
  const [termo, setTermo] = useState(normalizada);

  useEffect(() => {
    const timer = window.setTimeout(() => setTermo(normalizada), 250);
    return () => window.clearTimeout(timer);
  }, [normalizada]);

  const consultavel = !!token && valida && !atual;
  const consultando = consultavel && termo !== normalizada;
  const query = useQuery({
    queryKey: ["/api/empresas/tag-parceiro-disponivel", normalizada, empresaId],
    queryFn: ({ signal }) => consultarDisponibilidadeTagParceiro({
      tag: normalizada,
      token: token!,
      empresaId,
      signal,
    }),
    enabled: consultavel && !consultando,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  let status: EstadoDisponibilidadeTag;
  if (!valida) status = "invalida";
  else if (atual) status = "atual";
  else if (!token) status = "publico";
  else if (consultando || query.isPending || query.isFetching) status = "consultando";
  else if (query.isError) status = "erro";
  else if (query.data?.disponivel) status = "disponivel";
  else status = "ocupada";

  return { status, refetch: query.refetch };
}

const MENSAGENS: Record<EstadoDisponibilidadeTag, string> = {
  invalida: "Use 1 a 10 letras ou números; comece por uma letra.",
  atual: "Esta TAG já está vinculada ao Parceiro.",
  publico: "A disponibilidade será confirmada ao enviar o cadastro.",
  consultando: "Verificando disponibilidade…",
  disponivel: "TAG disponível.",
  ocupada: "Essa TAG já está sendo usada.",
  erro: "Não foi possível verificar agora. Tente novamente.",
};

export function TagParceiroField({
  value,
  onChange,
  availability,
  disabled = false,
  required = false,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  availability: { status: EstadoDisponibilidadeTag; refetch: () => unknown };
  disabled?: boolean;
  required?: boolean;
  testId?: string;
}) {
  const alert = availability.status === "ocupada" || availability.status === "erro";
  return (
    <div className="space-y-2">
      <Label htmlFor={testId ?? "tag-parceiro"}>TAG do Parceiro{required ? " *" : ""}</Label>
      <Input
        id={testId ?? "tag-parceiro"}
        data-testid={testId}
        value={value}
        onChange={(event) => onChange(normalizarTagParceiro(event.target.value))}
        maxLength={10}
        autoCapitalize="characters"
        autoComplete="off"
        required={required}
        disabled={disabled}
        aria-invalid={availability.status === "invalida" || availability.status === "ocupada" || undefined}
        aria-describedby={`${testId ?? "tag-parceiro"}-ajuda`}
      />
      <p id={`${testId ?? "tag-parceiro"}-ajuda`} role={alert ? "alert" : "status"} className={`text-xs ${availability.status === "ocupada" || availability.status === "invalida" || availability.status === "erro" ? "text-destructive" : "text-muted-foreground"}`}>
        {MENSAGENS[availability.status]}
        {availability.status === "erro" && !disabled && (
          <> <button type="button" className="underline underline-offset-2" onClick={() => void availability.refetch()}>Tentar novamente</button></>
        )}
      </p>
    </div>
  );
}
